/**
 * Image resize utility for client-side image processing
 *
 * Uses Canvas API to resize images proportionally before upload.
 * Extracted as pure function for testability (Canvas unavailable in JSDOM).
 */

export interface ResizeResult {
  base64: string; // Raw base64 without data URI prefix
  width: number;
  height: number;
  sizeBytes: number;
}

/**
 * Resize an image file to fit within max dimensions, with optional quality reduction.
 *
 * - Proportionally scales to fit within maxWidth/maxHeight
 * - For JPEG: iteratively reduces quality if output exceeds maxFileSize
 * - Strips data URI prefix, returns raw base64
 */
export async function resizeImage(
  file: File,
  maxWidth: number,
  maxHeight?: number,
  format: 'jpeg' | 'png' = 'jpeg',
  quality: number = 0.85,
  maxFileSize?: number
): Promise<ResizeResult> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);

      // Calculate proportional dimensions
      let width = img.width;
      let height = img.height;

      if (width > maxWidth) {
        height = Math.round((height * maxWidth) / width);
        width = maxWidth;
      }

      if (maxHeight && height > maxHeight) {
        width = Math.round((width * maxHeight) / height);
        height = maxHeight;
      }

      // Draw to canvas
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Failed to get canvas context'));
        return;
      }
      ctx.drawImage(img, 0, 0, width, height);

      const mimeType = format === 'jpeg' ? 'image/jpeg' : 'image/png';

      // For PNG, no quality iteration needed
      if (format === 'png') {
        const dataUrl = canvas.toDataURL(mimeType);
        const base64 = dataUrl.replace(/^data:image\/\w+;base64,/, '');
        const sizeBytes = Math.ceil(base64.length * 3 / 4);
        resolve({ base64, width, height, sizeBytes });
        return;
      }

      // JPEG: iterative quality reduction if output exceeds maxFileSize
      const qualitySteps = [quality, 0.75, 0.65, 0.50];
      for (const q of qualitySteps) {
        const dataUrl = canvas.toDataURL(mimeType, q);
        const base64 = dataUrl.replace(/^data:image\/\w+;base64,/, '');
        const sizeBytes = Math.ceil(base64.length * 3 / 4);

        if (!maxFileSize || sizeBytes <= maxFileSize) {
          resolve({ base64, width, height, sizeBytes });
          return;
        }
      }

      // Even at lowest quality, still too large — return it anyway
      // (backend will reject if over limit)
      const dataUrl = canvas.toDataURL(mimeType, qualitySteps[qualitySteps.length - 1]);
      const base64 = dataUrl.replace(/^data:image\/\w+;base64,/, '');
      const sizeBytes = Math.ceil(base64.length * 3 / 4);
      resolve({ base64, width, height, sizeBytes });
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };

    img.src = url;
  });
}
